import { Observable } from 'rxjs';

import { HttpClient, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import {
  ApiUrlInjectionToken,
  ModelSearchParams,
  PerformanceAccessoryAnswer,
  PerformanceEnvelop,
  PerformanceFilter,
  PerformanceFiltersPayload,
  Study,
  StudyAhriCertificationType,
  UnitPerformanceAccessoryAnswers,
  UnitsUpdateData,
} from '@mitsubishi/shared/data-access';
import { getRequestTraceHttpHeaders } from '@mitsubishi/shared/request-tracing';

import { FormlyFieldConfig } from '@ngx-formly/core';

import { ExternalUnit, SimulationRequest, SimulationResponse, StudyAddUnitToQuoteRequest } from '../models';
import {
  ExternalUnitWorkingConditionsPayload,
  UpdateExternalUnitWorkingConditionsParams,
} from '../models/requests/study-requests';

@Injectable({
  providedIn: 'root',
})
export class StudyService {
  private readonly baseUrl: string;

  constructor(
    private http: HttpClient,
    @Inject(ApiUrlInjectionToken) apiServerUrl: string
  ) {
    this.baseUrl = apiServerUrl + '/project';
  }

  createStudy(
    projectId: string,
    certified = false,
    organizationId: number,
    systemType?: StudyAhriCertificationType
  ): Observable<{ uuid: string }> {
    return this.http.post<{ uuid: string }>(`${this.baseUrl}/${projectId}/study`, {
      certified,
      systemType,
      organizationId,
    });
  }

  deleteStudy(projectId: string, studyId: string): Observable<null> {
    return this.http.delete<null>(`${this.baseUrl}/${projectId}/study/${studyId}`, {});
  }

  updateStudyName(projectId: string, studyId: string, name: string): Observable<null> {
    return this.http.patch<null>(`${this.baseUrl}/${projectId}/study/${studyId}/name`, { name });
  }

  updateStudyNote(projectId: string, studyId: string, note: string): Observable<{ uuid: string }> {
    return this.http.patch<{ uuid: string }>(`${this.baseUrl}/${projectId}/study/${studyId}/note`, { note });
  }

  updatePerformanceData(
    projectId: string,
    studyId: string,
    studySubcategoryId: string,
    workingConditionId: string,
    data: any,
    mergeConditions = false,
    requestTraceId?: string
  ): Observable<null> {
    const headers = getRequestTraceHttpHeaders(requestTraceId);

    return this.http.patch<null>(
      `${this.baseUrl}/${projectId}/study/${studyId}/subcategory/${studySubcategoryId}/working-condition/${workingConditionId}/data`,
      { data, mergeConditions },
      { headers }
    );
  }

  createUnit(
    projectId: string,
    studyId: string,
    modelSearchUnitId: string,
    requestTraceId?: string
  ): Observable<{ uuid: string }> {
    const headers = getRequestTraceHttpHeaders(requestTraceId);

    return this.http.post<{ uuid: string }>(
      `${this.baseUrl}/${projectId}/study/${studyId}/subcategory/unit`,
      {
        bleenId: modelSearchUnitId,
      },
      { headers }
    );
  }

  deleteUnit(projectId: string, studyId: string, subcategoryId: string, unitId: string): Observable<null> {
    return this.http.delete<null>(
      `${this.baseUrl}/${projectId}/study/${studyId}/subcategory/${subcategoryId}/unit/${unitId}`,
      {}
    );
  }

  deleteUnits(projectId: string, unitsId: string): Observable<null> {
    return this.http.delete<null>(`${this.baseUrl}/${projectId}/units?unitIds=${unitsId}`, {});
  }

  addAllUnitsToStudy(
    projectId: string,
    temporaryStudyId: string,
    parentWorkingConditionId?: string,
    requestTraceId?: string
  ): Observable<null> {
    const headers = getRequestTraceHttpHeaders(requestTraceId);
    const queryParams: Record<string, string> = parentWorkingConditionId ? { parentWorkingConditionId } : {};

    return this.http.post<null>(
      `${this.baseUrl}/${projectId}/temporary-study/${temporaryStudyId}/unit/all`,
      {},
      { params: queryParams, headers }
    );
  }

  toggleGoodForSelection(
    projectId: string,
    studyId: string,
    subcategoryId: string,
    unitId: string,
    performanceId: string
  ): Observable<null> {
    return this.http.patch<null>(
      `${this.baseUrl}/${projectId}/study/${studyId}/subcategory/${subcategoryId}/unit/${unitId}/performance/${performanceId}/good-for-selection`,
      {}
    );
  }

  getWorkingConditions(
    projectId: string,
    studyId: string,
    subcategoryId: number,
    productTypeId: number,
    workingConditionId: string,
    reversalCycleId?: number
  ): Observable<FormlyFieldConfig[]> {
    const req = {
      productTypeId: productTypeId,
      subcategoryId: subcategoryId,
      reversalCycleId: reversalCycleId,
    };
    return this.http.post<FormlyFieldConfig[]>(
      `${this.baseUrl}/${projectId}/study/${studyId}/subcategory/working-condition/${workingConditionId}`,
      req
    );
  }

  createWorkingCondition(
    projectId: string,
    studyId: string,
    subcategoryId: string,
    requestTraceId?: string
  ): Observable<null> {
    const headers = getRequestTraceHttpHeaders(requestTraceId);

    return this.http.post<null>(
      `${this.baseUrl}/${projectId}/study/${studyId}/subcategory/${subcategoryId}/working-condition`,
      {},
      { headers }
    );
  }

  updateWorkingCondition(
    projectId: string,
    studyId: string,
    subcategoryId: string,
    workingConditionId: string,
    name: string
  ): Observable<{ uuid: string }> {
    return this.http.patch<{ uuid: string }>(
      `${this.baseUrl}/${projectId}/study/${studyId}/subcategory/${subcategoryId}/working-condition/${workingConditionId}/name`,
      { name }
    );
  }

  deleteWorkingCondition(
    projectId: string,
    studyId: string,
    subcategoryId: string,
    workingConditionId: string
  ): Observable<null> {
    return this.http.delete<null>(
      `${this.baseUrl}/${projectId}/study/${studyId}/subcategory/${subcategoryId}/working-condition/${workingConditionId}`
    );
  }

  addUnitToQuote(projectId: string, selectionId: string, request: StudyAddUnitToQuoteRequest): Observable<null> {
    return this.http.post<null>(`${this.baseUrl}/${projectId}/selection/${selectionId}/unit-from-study`, request);
  }
  updateStudyUnitNote(
    projectId: string,
    studyId: string,
    subcategoryId: string,
    unitId: string,
    workingConditionId: string,
    note: string
  ): Observable<null> {
    return this.http.patch<null>(
      `${this.baseUrl}/${projectId}/study/${studyId}/subcategory/${subcategoryId}/unit/${unitId}/note`,
      {
        note,
        workingConditionId,
      }
    );
  }

  createMultipleUnits(
    projectId: string,
    studyId: string,
    groupValue: string | undefined,
    request: ModelSearchParams
  ): Observable<null> {
    return this.http.post<null>(`${this.baseUrl}/${projectId}/study/${studyId}/subcategory/units`, {
      groupValue,
      modelSearchRequest: request,
    });
  }

  deleteSubcategory(projectId: string, studyId: string, subcategoryId: string): Observable<null> {
    return this.http.delete<null>(
      `${this.baseUrl}/${projectId}/study/${studyId}/subcategory/${subcategoryId}`,

      {}
    );
  }

  updateAccessories(projectId: string, studyId: string, unitId: string, sessionId: string): Observable<null> {
    return this.http.post<null>(
      `${this.baseUrl}/${projectId}/study/${studyId}/subcategory/unit/${unitId}/accessory/${sessionId}`,
      {}
    );
  }

  getFilterConditions(projectId: string, studySubcategoryId: string): Observable<FormlyFieldConfig[]> {
    return this.http.get<FormlyFieldConfig[]>(
      `${this.baseUrl}/${projectId}/study/subcategory/${studySubcategoryId}/filter-condition`
    );
  }

  updateFilterConditionsData(
    projectId: string,
    studyId: string,
    studySubcategoryId: string,
    payload: PerformanceFiltersPayload,
    requestTraceId?: string
  ): Observable<null> {
    const headers = getRequestTraceHttpHeaders(requestTraceId);

    return this.http.patch<null>(
      `${this.baseUrl}/${projectId}/study/${studyId}/subcategory/${studySubcategoryId}/filter-condition`,
      payload,
      { headers }
    );
  }

  getPerformanceFilters(projectId: string, studyId: string, studySubcategoryId: string) {
    return this.http.get<PerformanceFilter[]>(
      `${this.baseUrl}/${projectId}/study/${studyId}/subcategory/${studySubcategoryId}/performance-filters`
    );
  }

  simulate(
    projectId: string,
    performanceId: string,
    unitId: string,
    simulation: SimulationRequest,
    requestTraceId?: string
  ): Observable<SimulationResponse> {
    const headers = getRequestTraceHttpHeaders(requestTraceId);
    return this.http.post<SimulationResponse>(
      `${this.baseUrl}/${projectId}/study/subcategory/performance/${performanceId}/unit/${unitId}/simulation`,
      simulation,
      { headers }
    );
  }

  assignPerformanceAccessories(
    projectId: string,
    studyId: string,
    performanceId: string,
    unitId: string,
    answers: PerformanceAccessoryAnswer[]
  ): Observable<null> {
    return this.http.post<null>(
      `${this.baseUrl}/${projectId}/study/${studyId}/subcategory/performance/${performanceId}/unit/${unitId}/performance-accessories`,
      answers
    );
  }

  assignMultiplePerformanceAccessories(
    projectId: string,
    studyId: string,
    unitAnswers: UnitPerformanceAccessoryAnswers[],
    requestTraceId?: string
  ): Observable<null> {
    const headers = getRequestTraceHttpHeaders(requestTraceId);
    return this.http.post<null>(
      `${this.baseUrl}/${projectId}/study/${studyId}/subcategory/performance-accessories`,
      unitAnswers,
      { headers }
    );
  }

  duplicateUnitAndAssignPerformanceAccessories(
    projectId: string,
    studyId: string,
    performanceId: string,
    unitId: string,
    answers: PerformanceAccessoryAnswer[],
    requestTraceId?: string
  ): Observable<null> {
    const headers = getRequestTraceHttpHeaders(requestTraceId);
    return this.http.post<null>(
      `${this.baseUrl}/${projectId}/study/${studyId}/subcategory/performance/${performanceId}/unit/${unitId}/duplicate`,
      answers,
      { headers }
    );
  }

  duplicateUnitAndAssignMultiplePerformanceAccessories(
    projectId: string,
    studyId: string,
    unitAnswers: UnitPerformanceAccessoryAnswers[],
    requestTraceId?: string
  ): Observable<null> {
    const headers = getRequestTraceHttpHeaders(requestTraceId);
    return this.http.post<null>(`${this.baseUrl}/${projectId}/study/${studyId}/subcategory/duplicate`, unitAnswers, {
      headers,
    });
  }

  checkModelsHasUpgrade(projectId: string, studyId: string): Observable<UnitsUpdateData> {
    return this.http.get<UnitsUpdateData>(`${this.baseUrl}/${projectId}/study/${studyId}/check-upgrade`);
  }

  upgradeModels(projectId: string, studyId: string, requestTraceId?: string): Observable<null> {
    const headers = getRequestTraceHttpHeaders(requestTraceId);

    return this.http.put<null>(`${this.baseUrl}/${projectId}/study/${studyId}/upgrade`, {}, { headers });
  }

  editUnitConfiguration(projectId: string, studyId: string, unitId: string): Observable<null> {
    return this.http.put<null>(
      `${this.baseUrl}/${projectId}/study/${studyId}/subcategory/working-condition/unit/${unitId}`,
      {}
    );
  }

  getExternalUnits(
    projectId: string,
    workingConditionId: string,
    unitId: string,
    workingConditions: any
  ): Observable<ExternalUnit[]> {
    return this.http.post<ExternalUnit[]>(
      `${this.baseUrl}/${projectId}/study/subcategory/working-condition/${workingConditionId}/unit/${unitId}/external-units`,
      workingConditions
    );
  }

  getExternalUnitsWorkingConditions(
    projectId: string,
    subcategoryId: string,
    queryParams?: { externalUnitId?: string; studyWorkingConditionId?: string }
  ): Observable<FormlyFieldConfig[]> {
    const params = {};
    if (queryParams?.externalUnitId) {
      params['externalUnitId'] = queryParams.externalUnitId;
    }
    if (queryParams?.studyWorkingConditionId) {
      params['studyWorkingConditionId'] = queryParams.studyWorkingConditionId;
    }

    return this.http.get<FormlyFieldConfig[]>(
      `${this.baseUrl}/${projectId}/study/subcategory/${subcategoryId}/external-units/working-conditions`,
      { params }
    );
  }

  addExternalUnit(payload: {
    projectId: string;
    studyId: string;
    unitId: string;
    bleenId: string;
    quantity: number;
    performanceId: string;
    workingConditions: any;
    requestTraceId?: string;
  }): Observable<null> {
    const body = {
      quantity: payload.quantity,
      bleenId: payload.bleenId,
      performanceId: payload.performanceId,
      workingCondition: payload.workingConditions,
    };
    const headers = getRequestTraceHttpHeaders(payload.requestTraceId);
    return this.http.post<null>(
      `${this.baseUrl}/${payload.projectId}/study/${payload.studyId}/subcategory/working-condition/unit/${payload.unitId}/external-unit`,
      body,
      { headers }
    );
  }

  updateLoadMode(unitId: string, loadMode: number): Observable<null> {
    return this.http.patch<null>(`${this.baseUrl}/study/subcategory/working-condition/unit/${unitId}/load-mode`, {
      loadMode,
    });
  }

  refreshPrice(projectId: string, studyId: string): Observable<null> {
    return this.http.get<null>(`${this.baseUrl}/${projectId}/study/${studyId}/refresh-price`);
  }

  createSelectionTemporaryStudy(
    projectId: string,
    selectionId: string,
    body: any,
    requestTraceId?: string
  ): Observable<{ uuid: string }> {
    const headers = getRequestTraceHttpHeaders(requestTraceId);

    return this.http.post<{ uuid: string }>(
      `${this.baseUrl}/${projectId}/selection/${selectionId}/temporary-study`,
      body,
      {
        headers,
      }
    );
  }

  // not connected to any endpoint
  createEnergyAnalysisTemporaryStudy(
    projectId: string,
    energyAnalysisId: string,
    body: any,
    requestTraceId?: string
  ): Observable<{ uuid: string }> {
    const headers = getRequestTraceHttpHeaders(requestTraceId);

    return this.http.post<{ uuid: string }>(
      `${this.baseUrl}/${projectId}/energy-analysis/${energyAnalysisId}/temporary-study`,
      body,
      {
        headers,
      }
    );
  }

  createStudyTemporaryStudy(
    projectId: string,
    studyId: string,
    body: any,
    requestTraceId?: string
  ): Observable<{ uuid: string }> {
    const headers = getRequestTraceHttpHeaders(requestTraceId);

    return this.http.post<{ uuid: string }>(`${this.baseUrl}/${projectId}/study/${studyId}/temporary-study`, body, {
      headers,
    });
  }

  getTemporaryStudyDetail(projectId: string, temporaryStudyId: string): Observable<Study> {
    return this.http.get<Study>(`${this.baseUrl}/${projectId}/temporary-study/${temporaryStudyId}`);
  }

  deleteTemporaryStudy(projectId: string, temporaryStudyId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${projectId}/temporary-study/${temporaryStudyId}`);
  }

  createSingleUnit(
    projectId: string,
    unitId: string,
    temporaryStudyId: string,
    workingConditionId: string,
    studyPerformanceId?: string,
    parentWorkingConditionId?: string,
    requestTraceId?: string
  ): Observable<any> {
    const headers = getRequestTraceHttpHeaders(requestTraceId);
    const queryParams: Record<string, string> = {};
    if (parentWorkingConditionId) {
      queryParams['parentWorkingConditionId'] = parentWorkingConditionId;
    }

    return this.http.post<any>(
      `${this.baseUrl}/${projectId}/temporary-study/${temporaryStudyId}/working-condition/${workingConditionId}/unit/${unitId}`,
      studyPerformanceId ? { studyPerformanceId } : undefined,
      { params: queryParams, headers }
    );
  }

  saveTemporaryStudy(projectId: string, temporaryStudyId: string, body): Observable<{ uuid: string }> {
    return this.http.post<{ uuid: string }>(
      `${this.baseUrl}/${projectId}/temporary-study/${temporaryStudyId}/promote`,
      body
    );
  }

  updateExternalUnitWorkingConditions(params: UpdateExternalUnitWorkingConditionsParams) {
    const headers = getRequestTraceHttpHeaders(params.requestTraceId);

    const body: ExternalUnitWorkingConditionsPayload = {
      workingConditionId: params.workingConditionId,
      data: params.data,
      addToAllUnits: params.addToAllUnits,
      addToUnitsWithoutExternalUnit: !params.addToAllUnits,
    };

    return this.http.post<null>(
      `${this.baseUrl}/${params.projectId}/study/${params.studyId}/subcategory/${params.studySubcategoryId}/external-units`,
      body,
      { headers }
    );
  }

  exportSubcategoryCsv(projectid: string, studyId: string, studySubcategoryId: string): Observable<HttpResponse<Blob>> {
    return this.http.get(
      `${this.baseUrl}/${projectid}/study/${studyId}/subcategory/${studySubcategoryId}/exportsubcategory`,
      {
        observe: 'response',
        responseType: 'blob',
      }
    );
  }

  showEnvelopes(
    projectId: string,
    studyId: string,
    workingConditionId: string,
    unitId: string
  ): Observable<PerformanceEnvelop[]> {
    return this.http.get<PerformanceEnvelop[]>(
      `${this.baseUrl}/${projectId}/study/${studyId}/subcategory/working-condition/${workingConditionId}/unit/${unitId}/envelopes`
    );
  }
}
